import React, { useMemo, useState } from 'react';

import { contactApis, dashboardContactApis } from '@apis/next/contactApis';
import infoIcon from '@assets/img/icons/info.svg';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { MobileBottomPopup } from '@components/MobileBottomPopup';
import { QueryContainer } from '@components/QueryContainer';
import { useBookingCreateContext } from '@components/utilities/BookingInquiry/BookingCreateContext';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { ContactFilter } from '@components/utilities/BookingInquiry/ContactFilter';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { fakeReceivers } from '@data/contacts';
import {
  contactForm,
  ContactForm as ContactFormData,
  contactFormInitialValues,
} from '@models/Contact';
import { Contact, ContactSearchQuery } from '@models/next/Contact';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createValidate, formStrings, useErrorMessage } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik, useField } from 'formik';

export const strings = createStrings({
  ja: {
    clientChooseReceiver: `${bookingInquiryStrings.receiver}一覧から選択する`,
    chooseReceiver: '納品先を選択する',
    CreateReceiver: '納品先を追加する',
    fillInWithNewReceiverInfo: `手入力で変更する`,
    receiverInfoTitle: '納品先情報',
    ok: '次へ',
    aboutChangingReceiver: `*予約後の${bookingInquiryStrings.receiver}の変更について`,
    aboutChangingReceiverTitle: `予約後の${bookingInquiryStrings.receiver}の変更について`,
    chooseReceiverPrompt: `${bookingInquiryStrings.receiver}を選択してください`,
    aboutChangingReceiverContent: () => (
      <>
        実際お届け時の納品先変更は可能です。その場合、運送費用の変更が伴います。
        <br />
        納品先の変更が必要な場合、左下の「...」アイコンをクリックして、D2Dカスタマーセンター
        へご連絡ください。
        <br />
        <br />
        また、変更は同一都道府県以内にお願いします。
        <br />
        他府県へ変更の場合、運送費用が大きく変わる可能性ありますので、ご了承をおね願い申しあげます。
        <br />
      </>
    ),
    createConsigneeDialogTitle: 'CNEE（輸入者）情報を入力してください',
    createReceiverDialogTitle: '納品先情報を入力してください',
    receiverCreateOK: 'OK',
    selectConsigneePrompt: '「CNEE（輸入者）情報」で CNEE を選択してください。',
    aboutReceiver: () => (
      <>
        納品先とCNEEの違いは？
        <br />
        一般的には、納品先とCNEE情報は同じです。CNEEは輸入者の会社情報です。
        <br />
        納品先は、輸入者社の荷物を受け取る場所です。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '1rem',
  },
  dialog: {
    maxWidth: '960px !important',
  },
  infoDialogContent: {
    ...common.middleText,
    ...common.fontWeight400,
    ...common.textAlignCenter,
    paddingTop: 50,
    paddingBottom: 50,
    '& a': {
      ...common.link,
    },
  },
  infoIconTitle: {
    marginRight: 10,
  },
  dashedDivider: {
    borderTop: `2px dashed ${theme.palette.grey[300]}`,
  },
  receiverChooseOptionsContainer: {
    ...common.flexInline,
    gap: '20px',
  },
  cneeInfoContainer: {
    ...common.flexInline,
    gap: '4rem',
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumn,
      gap: '1rem',
    },
  },
  contactItem: {
    ...common.flexColumn,
    gap: '0.875rem',
  },
  contactItemTitle: {
    ...common.middleText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
  },
  ButtonContainer: {
    ...common.flexInline,
    justifyContent: 'space-between',
    padding: '1rem 24px',
  },
  infoTips: {
    ...common.textGray700,
    ...common.smallerText,
    ...common.flexInline,
    ...common.itemsStart,
    lineHeight: '1.125rem',
    minHeight: '4rem',
  },
}));

export const ReceiverInfoInput: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isMobile = useIsMobile();
  const { isDashboard } = useBookingCreateContext();

  const [receiverField, , receiverHelpers] = useField<number | undefined>('receiver');
  const [consigneeField] = useField<number | undefined>('consignee');
  const [consigneeContactField] = useField<ContactFormData | undefined>('consignee_input');
  const [receiverContactField, , receiverContactHelpers] = useField<ContactFormData | undefined>(
    'receiver_input',
  );

  const error = useErrorMessage('receiver');

  const [receiverParams, setReceiverParams] = useState<ContactSearchQuery>({});

  const receiversSearchQueries = {
    ...receiverParams,
    owner_id: consigneeField.value,
    contact_type: 'receiver',
    ordering: ['-use_count'],
  } satisfies ContactSearchQuery;

  const receiversQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact', { queries: receiversSearchQueries })
    : contactApis.useGet('/contact', { queries: receiversSearchQueries });

  const receiverQueryParams = [
    {
      params: { id: receiverField.value! },
    },
    {
      enabled: !!receiverField.value,
    },
  ] as const;

  const receiverQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact/:id', ...receiverQueryParams)
    : contactApis.useGet('/contact/:id', ...receiverQueryParams);

  const contacts = useMemo(
    () => [...(receiversQuery.data ?? []), ...(isDashboard ? fakeReceivers : [])],
    [receiversQuery.data, isDashboard],
  );

  const initialReceiverValues = receiverContactField.value
    ? receiverContactField.value
    : contactFormInitialValues;

  const [receiverListOpen, setReceiverListOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [receiverCreateDialogOpen, setReceiverCreateDialogOpen] = useState(false);

  const isConsigneeManuallyInput = isDashboard && consigneeContactField.value;

  const renderReceiverChooseOptions = (change?: boolean) => {
    return (
      <div className={classes.receiverChooseOptionsContainer}>
        <Button
          data-testid="receiver-choose-create"
          color="primary"
          text={
            change
              ? formStrings.change
              : isConsigneeManuallyInput
              ? strings.CreateReceiver
              : strings.chooseReceiver
          }
          size="small"
          shape="round"
          type="button"
          onClick={() =>
            isConsigneeManuallyInput ? setReceiverCreateDialogOpen(true) : setReceiverListOpen(true)
          }
          disabled={typeof consigneeField.value !== 'number'}
        />
      </div>
    );
  };

  const renderReceiverEmpty = () => {
    return renderReceiverChooseOptions();
  };

  const renderReceiverSelected = () => {
    const receiverOrFake =
      receiverQuery.data ?? fakeReceivers.find((c) => c.id === receiverField.value);

    return (
      <div>
        <div className={classes.cneeInfoContainer}>
          {receiverContactField.value && <ContactDisplay contact={receiverContactField.value} />}
          {receiverOrFake && <ContactDisplay contact={receiverOrFake} />}
        </div>

        <div style={{ height: 14 }} />

        {renderReceiverChooseOptions(true)}
      </div>
    );
  };

  const renderChooseReceiverDialog = () => (
    <ActionableDialog
      open={receiverListOpen}
      onClose={() => {
        setReceiverListOpen(false);
      }}
      classes={{
        paper: classes.dialog,
      }}
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      <ActionableDialogTitle>{strings.chooseReceiverPrompt}</ActionableDialogTitle>
      <ActionableDialogContent>
        <QueryContainer style={{ height: 478 }} queries={[receiversQuery]}>
          <ContactFilter
            contacts={contacts}
            title={bookingInquiryStrings.receiver}
            selectedIds={receiverField.value ? [receiverField.value] : []}
            onChangeSelectedIds={(ids) => {
              receiverHelpers.setTouched(true);
              receiverHelpers.setValue(ids[ids.length - 1]);
            }}
            onAddContact={() =>
              window.open(
                `${url(
                  '/utilities/Contact/Consignees/ConsigneeDetails',
                )}?id=${consigneeField.value!}&action=createOwnedContact`,
              )
            }
            onEditContact={() =>
              window.open(
                `${url(
                  '/utilities/Contact/Consignees/ConsigneeDetails',
                )}?id=${consigneeField.value!}`,
              )
            }
            onRefresh={() => {
              receiversQuery.remove();
              receiversQuery.refetch();
            }}
            query={receiverParams}
            onChangeQuery={setReceiverParams}
            actions={isDashboard ? 'none' : 'add-list'}
            isEnglishNameRequired={!isDashboard}
            isZipcodeRequired
            editUrl={(contact: Contact) =>
              (isDashboard
                ? undefined
                : contact.owner &&
                  `${url('/utilities/Contact/Consignees/ConsigneeDetails')}?id=${
                    contact.owner
                  }&action=editWithScrollBottom`) || ''
            }
          />
        </QueryContainer>
      </ActionableDialogContent>
      <ActionableDialogContentDivider />
      <div className={classes.ButtonContainer}>
        <Button
          color="primary"
          text={formStrings.next}
          size="small"
          data-testid="close-contact-filter"
          disabled={!receiverField.value}
          onClick={() => {
            setReceiverListOpen(false);
          }}
          style={{ width: '100%' }}
        />
      </div>
    </ActionableDialog>
  );

  const renderReceiverCreateDialog = () => {
    return (
      <ActionableDialog
        open={receiverCreateDialogOpen}
        onClose={() => {
          setReceiverCreateDialogOpen(false);
        }}
        classes={{
          paper: classes.dialog,
        }}
        muiDialogProps={{
          disablePortal: false,
        }}
      >
        <Formik
          initialValues={initialReceiverValues}
          validate={createValidate(contactForm)}
          onSubmit={async (values) => {
            receiverHelpers.setValue(0);
            receiverHelpers.setTouched(true);
            receiverContactHelpers.setValue(values);
            receiverContactHelpers.setTouched(true);
            setReceiverCreateDialogOpen(false);
          }}
        >
          {({ handleSubmit }) => (
            <Form>
              <SubmittingOverlay />

              <ActionableDialogTitle textAlign="center">
                {strings.createReceiverDialogTitle}
              </ActionableDialogTitle>
              <ActionableDialogContent>
                <ContactForm zipcodeRequired contactType="receiver" />
              </ActionableDialogContent>

              <ActionableDialogContentDivider />
              <div style={{ padding: '1rem 24px' }} className={classes.flexCenter}>
                <Button
                  color="primary"
                  text={strings.receiverCreateOK}
                  size="small"
                  shape="round"
                  data-testid="close-contact-create"
                  onClick={() => {
                    handleSubmit();
                  }}
                  style={{ padding: '2px 36px' }}
                />
              </div>
            </Form>
          )}
        </Formik>
      </ActionableDialog>
    );
  };

  return (
    <div className={clsx(classes.container)}>
      {receiverField.value === undefined && renderReceiverEmpty()}
      {receiverField.value !== undefined && renderReceiverSelected()}
      <div style={{ height: 10 }} />

      {typeof consigneeField.value !== 'number' && (
        <div className={clsx(classes.textError, classes.smallerText)} style={{ marginBottom: 10 }}>
          {strings.selectConsigneePrompt}
        </div>
      )}

      {error != null && (
        <>
          <div
            className={clsx(classes.textError, classes.smallerText)}
            style={{ marginBottom: 10 }}
          >
            {error ?? 'Error'}
          </div>
        </>
      )}

      <div
        className={clsx(classes.link, classes.exSmallerText, classes.baseButton, classes.underline)}
        onClick={() => setInfoOpen(true)}
      >
        {strings.aboutChangingReceiver}
      </div>

      {receiverField.value === undefined && (
        <>
          <VerticalSpace height={10} />
          <div className={classes.infoTips}>
            <img src={infoIcon} style={{ height: 13.75, width: 13.75 }} alt="info" />
            <div style={{ width: 10 }} />
            {strings.aboutReceiver()}
          </div>
        </>
      )}

      {!isMobile && (
        <ActionableDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          classes={{
            paper: classes.dialog,
          }}
          muiDialogProps={{
            disablePortal: false,
          }}
        >
          <ActionableDialogTitle>
            <img src={infoIcon} alt="info" className={classes.infoIconTitle} />
            {strings.aboutChangingReceiverTitle}
          </ActionableDialogTitle>
          <ActionableDialogContent>
            <div className={classes.infoDialogContent}>
              {strings.aboutChangingReceiverContent()}
            </div>
          </ActionableDialogContent>
        </ActionableDialog>
      )}

      {isMobile && (
        <MobileBottomPopup
          open={infoOpen}
          title={
            <>
              <img
                alt="Info Icon"
                src={infoIcon}
                style={{ position: 'relative', top: 2, marginRight: 9 }}
              />
              {strings.aboutChangingReceiverTitle}
            </>
          }
          onClose={() => setInfoOpen(false)}
        >
          {strings.aboutChangingReceiverContent()}
        </MobileBottomPopup>
      )}

      {renderChooseReceiverDialog()}
      {renderReceiverCreateDialog()}
    </div>
  );
};
